import * as React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Button } from './Button';
import { cn } from './cn';
import { Icon } from './Icon';
import { Input } from './Input';
import { ScrollArea } from './ScrollArea';
import { Text } from './Typography';

// Closed transfer-list primitive: two columns (available / selected) with
// move-one and move-all actions plus a search field. Caller passes the full
// `options` and the controlled `values` array.
//
// Local vs remote search: if `onSearch` is omitted, the search input filters
// `options` locally by label.includes(term). If `onSearch` is provided,
// pressing Enter awaits `onSearch(term)` and the primitive paints its own
// loading state — caller is expected to fetch and re-pass `options` (e.g.
// against a remote API).
//
// Virtualization: both columns render only the visible window through
// @tanstack/react-virtual. Row stride is fixed (40px) and labels truncate
// to a single line with a `title=` tooltip on hover. This keeps the
// primitive snappy regardless of how many options the caller passes —
// 50 or 50,000 rows render at the same cost.

// Min height anchors the component in callsites that don't constrain
// its parent (catalog hero, dialogs without flex parents). When the
// parent does provide an explicit height (e.g. a flex-1 ancestor chain
// in a wizard step), the `tw-h-full` below lets the component grow to
// fill that available space — both vertically and horizontally — so
// "boxed-in at 400px in a 900px-tall step" never happens.
const MIN_CONTAINER_HEIGHT = '400px';
const ROW_HEIGHT = 40;
const ROW_OVERSCAN = 6;

export function TransferList({
    disabled = false,
    emptyMessage,
    limit,
    onChange,
    onSearch,
    options = [],
    searchPlaceholder = i18next.t('ui.transfer_list.filter_items'),
    values = []
}) {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isFetching, setIsFetching] = React.useState(false);
    const isRemote = Boolean(onSearch);

    const availableItems = React.useMemo(() => {
        const selectedSet = new Set(values.map(String));

        return options.filter(opt => !selectedSet.has(String(opt.value)));
    }, [options, values]);

    const filteredLeftItems = React.useMemo(() => {
        if (isRemote) {
            return availableItems;
        }

        const lowerSearch = searchTerm.toLowerCase();

        return availableItems.filter(item =>
            (item?.label || '').toLowerCase().includes(lowerSearch)
        );
    }, [availableItems, isRemote, searchTerm]);

    const rightItems = React.useMemo(() => values
        .map(id => options.find(opt => String(opt.value) === String(id)))
        .filter(Boolean)
        .reverse(), [options, values]);

    // Both columns get their own virtualizer because their visible
    // windows are independent. Viewport refs are forwarded into the
    // ScrollArea primitives via the `viewportRef` prop.
    const leftViewportRef = React.useRef(null);
    const rightViewportRef = React.useRef(null);

    const leftVirtualizer = useVirtualizer({
        count: filteredLeftItems.length,
        estimateSize: () => ROW_HEIGHT,
        getScrollElement: () => leftViewportRef.current,
        overscan: ROW_OVERSCAN
    });

    const rightVirtualizer = useVirtualizer({
        count: rightItems.length,
        estimateSize: () => ROW_HEIGHT,
        getScrollElement: () => rightViewportRef.current,
        overscan: ROW_OVERSCAN
    });

    // Reset scroll to top whenever the visible window shrinks — typing
    // in the search input narrows the list and a previously-deep scroll
    // position would leave the viewport blank. Cheaper than computing
    // whether the new size invalidates the old offset.
    React.useEffect(() => {
        leftVirtualizer.scrollToOffset(0);
    }, [searchTerm, leftVirtualizer]);

    const moveAllLeft = () => {
        if (disabled) {
            return;
        }

        onChange([]);
    };

    const moveAllRight = () => {
        if (disabled || values.length + filteredLeftItems.length > limit) {
            return;
        }

        const newIds = filteredLeftItems.map(item => String(item.value));

        onChange([...values, ...newIds]);
    };

    const moveLeft = (item) => {
        if (disabled) {
            return;
        }

        onChange(values.filter(id => String(id) !== String(item.value)));
    };

    const moveRight = (item) => {
        if (disabled || values.length >= limit) {
            return;
        }

        onChange([...values, item.value]);
    };

    const handleSearchEnter = async () => {
        if (!isRemote || disabled) {
            return;
        }

        setIsFetching(true);

        try {
            await onSearch(searchTerm);
        } finally {
            setIsFetching(false);
        }
    };

    const isMaxReached = values.length >= limit;
    const isListTooLarge = filteredLeftItems.length > limit;
    const isExceedingLimit = values.length + filteredLeftItems.length > limit;
    const canClearSelection = !disabled && !isFetching && rightItems.length > 0;
    const canSelectAll = !disabled && !isFetching && filteredLeftItems.length > 0 && !isListTooLarge && !isExceedingLimit;
    const interactionsDisabled = disabled || isFetching;

    const selectAllMessage = `${i18next.t('ui.select_all')} (${filteredLeftItems.length})`;
    const selectAllDisabledReason = isListTooLarge
        ? i18next.t('ui.transfer_list.too_many_choices')
        : isExceedingLimit
            ? i18next.t('ui.transfer_list.exceeds_limit', { max: limit })
            : disabled
                ? i18next.t('common.disabled', 'Disabled')
                : null;

    return (
        <div className="tw-flex tw-h-full tw-w-full tw-min-h-0 tw-flex-col">
            <div
                className="tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-1 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-input tw-bg-background"
                style={{ minHeight: MIN_CONTAINER_HEIGHT }}
            >
                <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-p-4 md:tw-flex-row md:tw-items-stretch">
                    {/* Left Column (Source) */}
                    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 md:tw-pr-3">
                        <Input
                            disabled={interactionsDisabled}
                            placeholder={searchPlaceholder}
                            type="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && isRemote) {
                                    e.preventDefault();
                                    handleSearchEnter();
                                }
                            }}
                        />
                        <Button
                            appearance="outline"
                            disabled={!canSelectAll}
                            fullWidth
                            icon="chevronRight"
                            iconAlign="edge"
                            iconPosition="trailing"
                            onClick={moveAllRight}
                            title={!canSelectAll ? selectAllDisabledReason : undefined}
                            tone="primary"
                        >
                            {selectAllMessage}
                        </Button>
                        {isFetching ? (
                            <div className="tw-flex tw-min-h-duallist-empty tw-flex-1 tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-px-6 tw-text-center">
                                <Text tone="muted" variant="control">
                                    {`${i18next.t('glossary.loading').capitalize()}…`}
                                </Text>
                            </div>
                        ) : filteredLeftItems.length > 0 ? (
                            <ScrollArea viewportRef={leftViewportRef}>
                                <div
                                    className="tw-relative tw-w-full"
                                    style={{ height: `${leftVirtualizer.getTotalSize()}px` }}
                                >
                                    {leftVirtualizer.getVirtualItems().map((virtualRow) => {
                                        const item = filteredLeftItems[virtualRow.index];
                                        return (
                                            <div
                                                key={item.value}
                                                className="tw-absolute tw-inset-x-0 tw-top-0"
                                                style={{
                                                    height: `${virtualRow.size}px`,
                                                    transform: `translateY(${virtualRow.start}px)`
                                                }}
                                            >
                                                <div
                                                    className={cn(
                                                        'tw-group tw-flex tw-h-9 tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-rounded-md',
                                                        interactionsDisabled || isMaxReached
                                                            ? 'tw-opacity-50 tw-cursor-not-allowed'
                                                            : 'hover:tw-bg-accent tw-cursor-pointer'
                                                    )}
                                                    onClick={interactionsDisabled || isMaxReached ? undefined : () => moveRight(item)}
                                                    title={item.label}
                                                >
                                                    <span className="tw-min-w-0 tw-flex-1 tw-truncate">
                                                        <Text tone="inherit" variant="body">{item.label}</Text>
                                                    </span>
                                                    {!interactionsDisabled && !isMaxReached && (
                                                        <span className="tw-flex-none tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                                                            <Icon icon="chevronRight" size="small" tone="inherit" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                        ) : (
                            <div className="tw-flex tw-min-h-duallist-empty tw-flex-1 tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-px-6 tw-text-center">
                                <Text tone="muted" variant="control">
                                    {emptyMessage}
                                </Text>
                            </div>
                        )}
                    </div>

                    <div className="tw-hidden md:tw-flex tw-flex-none tw-items-center">
                        <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-1">
                            <Icon icon="chevronRight" size="small" tone="muted" />
                            <Icon icon="chevronLeft" size="small" tone="muted" />
                        </div>
                    </div>

                    {/* Right Column (Selected) */}
                    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 md:tw-pl-3">
                        <div className="tw-flex tw-h-10 tw-w-full tw-items-center tw-rounded-md tw-bg-muted tw-px-3">
                            <Text variant="control">
                                {`${i18next.t('glossary.selected').capitalize()} (${rightItems.length} / ${limit})`}
                            </Text>
                        </div>
                        <Button
                            appearance="outline"
                            disabled={!canClearSelection}
                            fullWidth
                            icon="chevronLeft"
                            iconAlign="edge"
                            iconPosition="leading"
                            onClick={moveAllLeft}
                            tone="destructive"
                        >
                            {i18next.t('ui.clear_selection', 'Remove All')}
                        </Button>
                        {rightItems.length === 0 ? (
                            <div className="tw-flex tw-min-h-duallist-empty tw-flex-1 tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-px-6 tw-text-center">
                                <Text tone="muted" variant="control">
                                    {i18next.t('ui.transfer_list.no_items_selected')}
                                </Text>
                            </div>
                        ) : (
                            <ScrollArea viewportRef={rightViewportRef}>
                                <div
                                    className="tw-relative tw-w-full"
                                    style={{ height: `${rightVirtualizer.getTotalSize()}px` }}
                                >
                                    {rightVirtualizer.getVirtualItems().map((virtualRow) => {
                                        const item = rightItems[virtualRow.index];
                                        return (
                                            <div
                                                key={item.value}
                                                className="tw-absolute tw-inset-x-0 tw-top-0"
                                                style={{
                                                    height: `${virtualRow.size}px`,
                                                    transform: `translateY(${virtualRow.start}px)`
                                                }}
                                            >
                                                <div
                                                    className={cn(
                                                        'tw-group tw-flex tw-h-9 tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-rounded-md',
                                                        interactionsDisabled
                                                            ? 'tw-opacity-50 tw-cursor-not-allowed'
                                                            : 'hover:tw-bg-accent tw-cursor-pointer'
                                                    )}
                                                    onClick={interactionsDisabled ? undefined : () => moveLeft(item)}
                                                    title={item.label}
                                                >
                                                    <div className="tw-flex tw-h-5 tw-w-5 tw-flex-none tw-items-center tw-justify-center">
                                                        <span className={interactionsDisabled ? 'tw-opacity-50' : 'tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity'}>
                                                            <Icon icon="chevronLeft" size="small" tone="inherit" />
                                                        </span>
                                                    </div>
                                                    <span className="tw-min-w-0 tw-flex-1 tw-truncate">
                                                        <Text variant="body">{item.label}</Text>
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
